import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { cn } from '@seen/ui'
import type { JSX } from 'react'
import { RadioGroup } from '#src/components/core/radio-group/RadioGroup.tsx'

export interface SegmentedControlOption<T extends string> {
  value: T
  label: string
}

export interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedControlOption<T>[]
  // `undefined` renders with nothing selected.
  value: T | undefined
  onChange: (value: T) => void
  disabled?: boolean
  label: string
  className?: string
}

// A single-select row of joined buttons. The 1px seams between options are the
// container's background showing through the `gap-px`, so the group reads as
// one control with no double borders.
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled,
  label,
  className,
}: SegmentedControlProps<T>): JSX.Element {
  return (
    <RadioGroup<string>
      // No option carries the empty value, so it renders nothing selected
      // while staying controlled.
      value={value ?? ''}
      onChange={(next) => onChange(next as T)}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'flex h-9 gap-px overflow-hidden rounded border border-border bg-border',
        className
      )}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          className={cn(
            'flex min-w-0 flex-1 items-center justify-center p-1 text-xs ring-offset-background focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            value === option.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-background text-muted-foreground'
          )}
        >
          {option.label}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroup>
  )
}
